import React from 'react'
import { formatInTimeZone } from 'date-fns-tz'
import { Box, Flex, Text } from '@chakra-ui/react'

import {
    PatientFieldsFragment,
    PatientPimsReminderFieldsFragment,
    useGetPimsRemindersByPatientQuery,
} from '~graphql/generated/graphql'
import { useClinic } from '~contexts/clinic-context'
import SharedStack from '~components/shared/wrappers/shared-stack'
import AsyncWrapper from '~components/shared/wrappers/async-wrapper'
import { groupRemindersByReminderType } from '~utils/reminder-helpers'
import SharedHelpText from '~components/shared/form/shared-help-text'
import ReminderStatusTag from '~components/shared/tags/reminder-status-tag'
import EmptyState from '~components/shared/empty-state'
import SyncPatientRemindersButton from '~components/shared/patient/sync-reminders-from-pims-button'

interface Props {
    patient: PatientFieldsFragment
}

export default function PatientPimsReminders({ patient }: Props) {
    const { clinic } = useClinic()

    const { data, loading, error } = useGetPimsRemindersByPatientQuery({
        variables: {
            clinicId: clinic?.id || '',
            patientPmsId: patient.pms_id || '',
            page: 0,
            pageSize: 1000,
            orderBy: {
                column: 'send_date',
                isAscending: true,
            },
        },
        skip: !clinic || !patient.pms_id,
    })

    const reminders = (data?.pimsRemindersByPatient || []).filter(
        r => !r.is_completed && r.is_active,
    )

    const groupedReminders = groupRemindersByReminderType(reminders)

    return (
        <Box w="full">
            <Flex justify="flex-end" mb={2}>
                <SyncPatientRemindersButton patient={patient} />
            </Flex>
            <AsyncWrapper
                loading={loading}
                empty={groupedReminders.length === 0}
                error={!!error}
                emptyComponent={
                    <Box w="full" bg="gray.50" px={4} py={3} rounded="xl">
                        <EmptyState
                            title="No reminders found"
                            subtitle=" "
                            py={6}
                        />
                    </Box>
                }
            >
                <SharedStack>
                    {groupedReminders?.map(reminder => (
                        <ReminderItem key={reminder.id} reminder={reminder} />
                    ))}
                </SharedStack>
            </AsyncWrapper>
        </Box>
    )
}

function ReminderItem({
    reminder,
}: {
    reminder: PatientPimsReminderFieldsFragment
}) {
    const { clinic } = useClinic()
    const timeZone = clinic?.time_zone || 'America/New_York'

    const dueDate = reminder.display_due_date || reminder.send_date
    const reminderDueDate = dueDate
        ? formatInTimeZone(dueDate, timeZone, 'PP')
        : null

    return (
        <Box
            w="full"
            rounded="xl"
            bg="gray.50"
            _dark={{ bg: 'blackAlpha.300' }}
            px={4}
            py={3}
        >
            <Flex w="full" justifyContent="space-between">
                <SharedStack spacing={1} fit align="flex-start">
                    <Text fontWeight="semibold" fontSize="sm">
                        {reminder.reminder_type?.display_name ||
                            reminder.reminder_type?.reminder_type_name ||
                            reminder.reminder_desc}
                    </Text>
                    {!!reminder.reminder_type && (
                        <SharedHelpText>
                            {reminder.reminder_desc}
                        </SharedHelpText>
                    )}
                </SharedStack>
                <SharedStack spacing={1} fit align="flex-end">
                    <ReminderStatusTag reminder={reminder} />
                    {reminderDueDate && (
                        <SharedHelpText>{`Due ${reminderDueDate}`}</SharedHelpText>
                    )}
                </SharedStack>
            </Flex>
        </Box>
    )
}
